import { type FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import {
  AnimatePresence,
  motion,
  transform,
  type Transition,
} from 'framer-motion';

import { getImage } from '@/common/lib/images';
import { playSound } from '@/common/lib/soundEffect';
import { currencyImages } from '@/components/helpers/imagesImports';
import { StringXSmall } from '@/components/Typography/Typography';
import { Picture } from '@/components/ui/Picture/Picture';
import { useAppSelector } from '@/redux/store';

import { getRandomTextByRewardKind } from './config';

import css from './styles.module.scss';

import boxReawardPng from '/img/dice-game/box-reward-bg.png';
import boxReawardWebp from '/img/dice-game/box-reward-bg.webp';
import caseRewardPng from '/img/dice-game/case.png';
import caseRewardWebp from '/img/dice-game/case.webp';
import characterPng from '/img/dice-game/character.png';
import characterWebp from '/img/dice-game/character.webp';
import dialogeSvg from '/img/dice-game/dialoge.svg';
import letterRewardPng from '/img/dice-game/letter.png';
import letterRewardWebp from '/img/dice-game/letter.webp';
import lightEffectPng from '/img/modals/light.png';
import lightEffectWebp from '/img/modals/light.webp';

const BoxReward: FC = () => {
  const { t } = useTranslation();
  const boxReward = useAppSelector((state) => state.diceGame.boxReward);

  const text = getRandomTextByRewardKind(boxReward);

  useEffect(() => {
    playSound('win/light');
  }, []);

  const calcHeightByOrigin = transform([0, 926], ['0vh', '100vh']);

  const rewardKind = {
    box: {
      png: boxReawardPng,
      webp: boxReawardWebp,
      className: 'bottom-0 aspect-[350/248] w-[81.77%]',
    },
    case: {
      png: caseRewardPng,
      webp: caseRewardWebp,
      className: 'aspect-square w-[84.11%] -bottom-[6.77%]',
    },
    letter: {
      png: letterRewardPng,
      webp: letterRewardWebp,
      className: 'aspect-[380/430] w-[88.78%] -bottom-[13.08%]',
    },
  };

  const getRewardPicture = (
    item: string,
  ):
    | { img: string; webp?: string; pngRight?: string; webpRight?: string }
    | undefined => {
    switch (item) {
      case 'cash': {
        return {
          img: currencyImages.cash.large.png,
          webp: currencyImages.cash.large.webp,
        };
      }
      case 'tgem': {
        return {
          img: currencyImages.tgem.large.png,
          webp: currencyImages.tgem.large.webp,
        };
      }
      default: {
        return getImage({ name: item, field: 'icon' });
      }
    }
  };

  const reward = rewardKind.box;

  const transition: Transition = {
    type: 'spring',
    stiffness: 600,
    mass: 1,
    damping: 35,
  };

  return (
    <div className="w-full h-full px-10">
      <div className="relative w-full h-full flex flex-col justify-end">
        <div
          style={{
            height: calcHeightByOrigin(464),
            bottom: calcHeightByOrigin(68),
          }}
          className="absolute left-0 w-full"
        >
          <AnimatePresence propagate>
            <motion.div
              initial={{ x: '-6rem', opacity: 0 }}
              animate={{ x: '-2.5rem', opacity: 1 }}
              exit={{
                x: '-6rem',
                opacity: 0,
                transition,
              }}
              transition={transition}
              className="relative w-full h-full"
            >
              <Picture
                className="absolute h-full bottom-0 aspect-[234/464]"
                img={characterPng}
                webp={characterWebp}
                alt="character"
              />
              <div
                style={{
                  top: calcHeightByOrigin(30),
                  left: calcHeightByOrigin(178),
                  backgroundImage: `url(${dialogeSvg})`,
                }}
                className={css.dialoge}
              >
                {text && <StringXSmall>{t(text)}</StringXSmall>}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
        <AnimatePresence propagate>
          <motion.div
            initial={{ y: '1rem', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{
              y: '1rem',
              opacity: 0,
              transition,
            }}
            transition={transition}
            className="relative w-full flex flex-col items-center"
          >
            <Picture
              img={reward.png}
              webp={reward.webp}
              className={cn(
                'absolute z-[1] left-1/2 -translate-x-1/2',
                reward.className,
              )}
              alt="box reward"
            />
            <div className="absolute w-[118.69%] left-1/2 -translate-x-1/2 aspect-[508/363] -bottom-[28.03%]">
              <div className="relative w-full h-full">
                <Picture
                  className="opacity-30 w-full"
                  img={lightEffectPng}
                  webp={lightEffectWebp}
                  alt="light"
                />
                <div className={css.effect}></div>
              </div>
            </div>
            <div
              style={{ width: calcHeightByOrigin(176), maxWidth: 176 }}
              className="relative h-[min(19.87svh,_184px)] mx-auto"
            >
              <AnimatePresence propagate>
                {boxReward &&
                  Object.entries(boxReward)
                    .filter((_, index) => !index)
                    .map(([item, value], index) => {
                      const image = getRewardPicture(item);

                      return (
                        <motion.div
                          key={index}
                          initial={{ y: '13.82vh', opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{ y: '13.82vh', opacity: 0, transition }}
                          transition={transition}
                          className="flex items-center bottom-full flex-col absolute z-[2]"
                        >
                          <Picture
                            style={{
                              height: calcHeightByOrigin(176),
                              maxHeight: 176,
                            }}
                            className="aspect-square"
                            img={image?.img}
                            webp={image?.webp}
                            alt={item}
                          />
                          <AnimatePresence propagate>
                            <motion.span
                              initial={{ y: '-4rem', opacity: 0 }}
                              animate={{ y: 0, opacity: 1, transition }}
                              exit={{ y: '-4rem', opacity: 0 }}
                              transition={transition}
                              className={cn(
                                'font-extrabold text-[2.5rem] leading-none relative',
                                value > 0 && css.plus,
                                value < 0 && css.minus,
                                value < 0 && 'text-red',
                              )}
                            >
                              {Math.abs(value)}
                            </motion.span>
                          </AnimatePresence>
                        </motion.div>
                      );
                    })}
              </AnimatePresence>
            </div>
            {/* <MainButton
              style={{ marginTop: calcHeightByOrigin(160) }}
              className="relative z-[2]"
              text={t('Claim')}
              onClick={() => {
                if (boxReward) {
                  dispatch(claimDiceGameBoxRewards(boxReward));
                }
                dispatch(setActiveBoard('dice'));
                dispatch(setBoxReward(null));
              }}
            /> */}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export { BoxReward };
